/* eslint-disable max-lines */
import {
  OnResumeSuccessHandler,
  OnTokenizeSuccessHandler,
} from '@primer-io/checkout-web'
import {
  PaymentMethodResult,
  PaymentRequestPaymentMethodEvent,
  Stripe,
  StripeCardNumberElement,
  StripeError,
} from '@stripe/stripe-js'
import { paymentApi, userApi } from 'api'
import i18n from 'i18next'

import {
  setErrorAction,
  setIsPrimerRetryProcessing,
  startFetching,
  stopFetching,
} from 'root-redux/actions/common'
import {
  savePlanAdditionsAction,
  sendUserConfigAction,
  setIsSubscriptionUpgradedAction,
  updateUserConfigAction,
} from 'root-redux/actions/user'
import {
  selectCurrentVariantCohort,
  selectCurrentVariantParentCohort,
  selectScreenName,
  selectStripeAccountId,
  selectStripeAccountName,
} from 'root-redux/selects/common'
import {
  selectPrimerProcessorName,
  selectUUID,
  selectUserConfigData,
  selectUserPaymentMethod,
  selectUserPaymentSystem,
} from 'root-redux/selects/user'

import { createIntroOfferProductId } from 'helpers/createIntroOfferProductId'
import { sendAddToCartEvents } from 'helpers/sendAddToCartEvents'

import {
  DEFAULT_CARDHOLDER_NAME,
  PRIMER_PAYMENT_ERRORS,
  PaymentMethod,
  PaymentSystem,
} from 'modules/purchase/constants'
import { getStripePurchaseFailedEventParams } from 'modules/purchase/helpers/getStripePurchaseFailedEventParams'
import { getStripePurchaseStartedEventParams } from 'modules/purchase/helpers/getStripePurchaseStartedEventParams'
import { getStripePurchaseSuccessEventParams } from 'modules/purchase/helpers/getStripePurchaseSuccessEventParams'
import {
  checkIsRetryAllowed,
  getPaymentFailedError,
  getRedirectUrl,
  handleGoaffproRefPurchase,
  logFailedPayment,
  logSuccessfulPayment,
} from 'modules/purchase/helpers/rootHelpers'
import { IPrimerSubscriptionBackupConfig } from 'modules/purchase/types'

import { IDynamicDiscount } from 'models/payment.model'
import { IAction, IAppState, TAppDispatchThunk } from 'models/store.model'
import { ISubscription } from 'models/subscriptions.model'

import { eventLogger } from 'services/eventLogger.service'

import { UPSELL_PAGES_MAP } from 'page-constants'
import { CENTS_IN_DOLLAR, Cohort, PlanAddition } from 'root-constants'

import {
  selectCreatedSubscriptionId,
  selectCurrency,
  selectInAppId,
  selectIsFirstPaymentRetryPassed,
  selectIsPrintablePdfEnabled,
  selectPaymentClientSecret,
  selectPaymentMethod,
  selectPrimerClientSessionToken,
  selectSubscriptionFullPrice,
  selectSubscriptionLookupKey,
  selectSubscriptionPeriodName,
  selectSubscriptionPeriodQuantity,
  selectSubscriptionTrialPeriodDays,
  selectSubscriptionTrialPeriodPrice,
  selectTrialPeriodDays,
  selectUpgradeSubscriptionFullPrice,
  selectUpgradeSubscriptionPrice,
  selectUserPaymentId,
} from '../selects/common'

const MODULE_NAME = 'PURCHASE'

export const SET_SUBSCRIPTION = `${MODULE_NAME}/SET_SUBSCRIPTION`
export const SET_TAX_AMOUNT = `${MODULE_NAME}/SET_TAX_AMOUNT`
export const PURCHASE = `${MODULE_NAME}/PURCHASE`
export const CHECK_3D_SECURE = `${MODULE_NAME}/CHECK_3D_SECURE`
export const SET_3D_SECURE_IFRAME_URL = `${MODULE_NAME}/SET_3D_SECURE_IFRAME_URL`
export const RESET_3D_SECURE_IFRAME_URL = `${MODULE_NAME}/RESET_3D_SECURE_IFRAME_URL`
export const SET_PAYMENT_CLIENT_SECRET = `${MODULE_NAME}/SET_PAYMENT_CLIENT_SECRET`
export const SET_TRIAL_PERIOD_DAYS = `${MODULE_NAME}/SET_TRIAL_PERIOD_DAYS`
export const SET_CREATED_SUBSCRIPTION_ID = `${MODULE_NAME}/SET_CREATED_SUBSCRIPTION_ID`
export const CHECK_PAYMENT_REQUEST_BUTTON = `${MODULE_NAME}/CHECK_PAYMENT_REQUEST_BUTTON`
export const CHECK_PAYPAL_REQUEST_BUTTON = `${MODULE_NAME}/CHECK_PAYPAL_REQUEST_BUTTON`
export const SET_IS_SUBSCRIPTION_UPGRADED = `${MODULE_NAME}/SET_IS_UPGRADED`
export const GET_PRIMER_CLIENT_SESSION_TOKEN = `${MODULE_NAME}/GET_PRIMER_CLIENT_SESSION_TOKEN`
export const SET_PRIMER_CLIENT_SESSION_TOKEN = `${MODULE_NAME}/SET_PRIMER_CLIENT_SESSION_TOKEN`
export const SET_PRIMER_PAYMENT_ID = `${MODULE_NAME}/SET_PRIMER_PAYMENT_ID`
export const UPDATE_PRIMER_CLIENT_SESSION = `${MODULE_NAME}/UPDATE_PRIMER_CLIENT_SESSION`
export const CANCEL_PRIMER_CLIENT_SESSION_DISCOUNT = `${MODULE_NAME}/CANCEL_PRIMER_CLIENT_SESSION_DISCOUNT`
export const SET_PAYMENT_METHOD = `${MODULE_NAME}/SET_PAYMENT_METHOD`
export const SET_BACKUP_PRIMER_SUBSCRIPTION_CONFIG = `${MODULE_NAME}/SET_BACKUP_PRIMER_SUBSCRIPTION_CONFIG`
export const SET_IS_FIRST_PAYMENT_RETRY_PASSED = `${MODULE_NAME}/SET_IS_FIRST_PAYMENT_RETRY_PASSED`
export const SET_DYNAMIC_DISCOUNT = `${MODULE_NAME}/SET_DYNAMIC_DISCOUNT`
export const SET_IS_PRINTABLE_PDF_ENABLED = `${MODULE_NAME}/SET_IS_PRINTABLE_PDF_ENABLED`

const set3DSecureIframeUrlAction = (payload: string): IAction<string> => ({
  type: SET_3D_SECURE_IFRAME_URL,
  payload,
})

const reset3DSecureIframeUrlAction = (): IAction<any> => ({
  type: RESET_3D_SECURE_IFRAME_URL,
})

const setPaymentClientSecretAction = (payload: string): IAction<string> => ({
  type: SET_PAYMENT_CLIENT_SECRET,
  payload,
})

const setTrialPeriodDaysAction = (payload: number): IAction<number> => ({
  type: SET_TRIAL_PERIOD_DAYS,
  payload,
})

const setCreatedSubscriptionIdAction = (payload: string): IAction<string> => ({
  type: SET_CREATED_SUBSCRIPTION_ID,
  payload,
})

const getErrorActionPayload = ({ type, message }: StripeError): string =>
  message || type

export const setSelectedSubscriptionAction = (
  payload: ISubscription | null,
): IAction<ISubscription | null> => ({
  type: SET_SUBSCRIPTION,
  payload,
})

export const setTaxAmountAction = (payload: number): IAction<number> => ({
  type: SET_TAX_AMOUNT,
  payload,
})

export const setDynamicDiscountAction = (
  payload: IDynamicDiscount,
): IAction<IDynamicDiscount> => ({
  type: SET_DYNAMIC_DISCOUNT,
  payload,
})

export const setIsPrintablePdfEnabled = (
  payload: boolean,
): IAction<boolean> => ({
  type: SET_IS_PRINTABLE_PDF_ENABLED,
  payload,
})

export const setPaymentMethodAction = (
  payload: PaymentMethod,
): IAction<PaymentMethod> => ({
  type: SET_PAYMENT_METHOD,
  payload,
})

export const setPrimerClientSessionTokenAction = (
  payload: string,
): IAction<string> => ({
  type: SET_PRIMER_CLIENT_SESSION_TOKEN,
  payload,
})

export const setPrimerPaymentId = (payload: string): IAction<string> => ({
  type: SET_PRIMER_PAYMENT_ID,
  payload,
})

export const setBackupPrimerSubscriptionConfigAction = (
  payload: IPrimerSubscriptionBackupConfig | null,
): IAction<IPrimerSubscriptionBackupConfig | null> => ({
  type: SET_BACKUP_PRIMER_SUBSCRIPTION_CONFIG,
  payload,
})

export const setIsFirstPaymentRetryPassedAction = (
  payload: boolean,
): IAction<boolean> => ({
  type: SET_IS_FIRST_PAYMENT_RETRY_PASSED,
  payload,
})

export function getPrimerClientSessionTokenAction(): any {
  return async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ) => {
    const state = getState()
    const uuid = selectUUID(state)
    const trialPeriodDays = selectTrialPeriodDays(state)
    const cohort = selectCurrentVariantCohort(state) as Cohort
    const planId = selectInAppId(state)

    dispatch(startFetching(GET_PRIMER_CLIENT_SESSION_TOKEN))

    const response = await paymentApi.getPrimerClientSessionToken({
      uuid,
      planId: Number(planId),
      trialPeriodDays,
      cohort,
    })

    if (response.success && response.data) {
      dispatch(setPrimerClientSessionTokenAction(response.data.client_token))

      dispatch(
        sendUserConfigAction({
          processor_name: response.data.processor_name,
        }),
      )
      dispatch(
        updateUserConfigAction({
          processorName: response.data.processor_name,
        }),
      )
    }

    dispatch(stopFetching(GET_PRIMER_CLIENT_SESSION_TOKEN))
  }
}

export function cancelPrimerClientSessionDiscount({
  clientToken,
}: {
  clientToken: string
}): any {
  return async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ) => {
    const state = getState()
    const uuid = selectUUID(state)
    dispatch(startFetching(CANCEL_PRIMER_CLIENT_SESSION_DISCOUNT))

    try {
      const response = await paymentApi.updatePrimerClientSession({
        uuid,
        clientToken,
      })

      dispatch(stopFetching(CANCEL_PRIMER_CLIENT_SESSION_DISCOUNT))

      if (!response.success && response?.data?.error) {
        dispatch(setErrorAction(i18n.t(PRIMER_PAYMENT_ERRORS.COMMON_ERROR)))

        return
      }
    } catch (error: any) {
      dispatch(stopFetching(CANCEL_PRIMER_CLIENT_SESSION_DISCOUNT))
      dispatch(setErrorAction(error.toString()))
      dispatch(stopFetching(PURCHASE))
    }
  }
}

export function updatePrimerClientSessionAction(
  retryCallback: () => void,
): any {
  return async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ) => {
    const state = getState()
    const uuid = selectUUID(state)
    const clientToken = selectPrimerClientSessionToken(state)
    const paymentId = selectUserPaymentId(state)
    dispatch(startFetching(UPDATE_PRIMER_CLIENT_SESSION))

    try {
      const response = await paymentApi.updatePrimerClientSession({
        uuid,
        paymentId,
        clientToken,
      })

      dispatch(stopFetching(UPDATE_PRIMER_CLIENT_SESSION))

      if (response.success && response.data && response.data.should_retry) {
        dispatch(stopFetching(PURCHASE))
        dispatch(setPrimerClientSessionTokenAction(response.data.client_token))

        await dispatch(setIsPrimerRetryProcessing(true))

        retryCallback()

        return
      }

      const error = i18n.t(
        PRIMER_PAYMENT_ERRORS[response.data.payment_error] ||
          PRIMER_PAYMENT_ERRORS.COMMON_ERROR,
      )

      dispatch(setIsPrimerRetryProcessing(false))
      dispatch(setErrorAction(error))
      dispatch(stopFetching(UPDATE_PRIMER_CLIENT_SESSION))
      dispatch(stopFetching(PURCHASE))
    } catch (error: any) {
      dispatch(setIsPrimerRetryProcessing(false))
      dispatch(setErrorAction(error.toString()))
      dispatch(stopFetching(PURCHASE))
      dispatch(stopFetching(UPDATE_PRIMER_CLIENT_SESSION))
    }
  }
}

export function primerConfirmPaymentAction(
  paymentId: string,
  handler: OnTokenizeSuccessHandler,
): any {
  return async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ) => {
    const state = getState()
    const uuid = selectUUID(state)
    const paymentMethod = selectPaymentMethod(state)
    const processorName = selectPrimerProcessorName(state)

    const commonPurchaseFailedParams = getStripePurchaseFailedEventParams(state)

    try {
      const response = await paymentApi.confirmPrimerSubscription({
        uuid,
        paymentId,
      })

      if (!response.data && !window.navigator.onLine) {
        dispatch(setErrorAction(i18n.t(PRIMER_PAYMENT_ERRORS.COMMON_ERROR)))
        dispatch(stopFetching(PURCHASE))
        dispatch(setIsPrimerRetryProcessing(false))
        return
      }

      if (!response.success && response?.data?.error) {
        eventLogger.logPurchaseFailed({
          ...commonPurchaseFailedParams,
          error: response?.data?.error,
          paymentSystem: PaymentSystem.PRIMER,
          paymentMethod,
          processorName,
        })
        dispatch(setErrorAction(response?.data?.error))
        dispatch(stopFetching(PURCHASE))
        dispatch(setIsPrimerRetryProcessing(false))

        handler.handleFailure(response?.data?.error)

        return
      }

      dispatch(stopFetching(PURCHASE))
      dispatch(setIsPrimerRetryProcessing(false))
      handler.handleSuccess()
    } catch (error: any) {
      dispatch(setErrorAction(error.toString()))
      dispatch(setIsPrimerRetryProcessing(false))
      dispatch(stopFetching(PURCHASE))
      handler.handleFailure(error.toString())
    }
  }
}

export function primerPurchaseAction(
  token: string,
  handler: OnTokenizeSuccessHandler,
  retryPaymentCallback: () => void,
): any {
  return async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ) => {
    const state = getState()
    const uuid = selectUUID(state)
    const paymentMethod = selectPaymentMethod(state)
    const cohort = selectCurrentVariantCohort(state) as Cohort
    const trialPeriodDays = selectSubscriptionTrialPeriodDays(state)
    const currentPrice = selectSubscriptionFullPrice(state)
    const trialPrice = selectSubscriptionTrialPeriodPrice(state)
    const currency = selectCurrency(state)
    const periodName = selectSubscriptionPeriodName(state)
    const periodQuantity = selectSubscriptionPeriodQuantity(state)
    const priceId = selectSubscriptionLookupKey(state)
    const isFirstPaymentRetryPassed = selectIsFirstPaymentRetryPassed(state)
    const parentCohort = selectCurrentVariantParentCohort(state)
    const config = selectUserConfigData(state)
    const hasPrintablePdf = selectIsPrintablePdfEnabled(state)
    const cohortToUse = parentCohort || cohort || Cohort.LUVLY_INTRO_FAST
    const processorName = selectPrimerProcessorName(state)

    let predictedLtv

    const commonPurchaseSuccessParams =
      getStripePurchaseSuccessEventParams(state)
    const commonPurchaseFailedParams = getStripePurchaseFailedEventParams(state)

    dispatch(startFetching(PURCHASE))

    if (cohortToUse === Cohort.LUVLY_PRINTABLE && hasPrintablePdf) {
      dispatch(savePlanAdditionsAction([PlanAddition.PRINTABLE_PDF]))
    }

    try {
      const response = await paymentApi.createPrimerSubscription({
        uuid,
        token,
        cohort,
      })

      if (!response.data && !window.navigator.onLine) {
        dispatch(setErrorAction(i18n.t(PRIMER_PAYMENT_ERRORS.COMMON_ERROR)))
        dispatch(stopFetching(PURCHASE))
        dispatch(setIsPrimerRetryProcessing(false))
        return
      }

      if (!response.data.payment.is_success) {
        const error = {
          type: response.data.payment.status_reason.type,
          description: response.data.payment.status_reason.message,
          code: response.data.payment.status_reason.code,
        }

        eventLogger.logPurchaseFailed({
          ...commonPurchaseFailedParams,
          paymentMethod,
          error,
          paymentSystem: PaymentSystem.PRIMER,
          processorName,
        })

        handler.handleFailure(error.description)

        dispatch(
          setErrorAction(
            i18n.t(
              PRIMER_PAYMENT_ERRORS[
                response?.data.payment.status_reason.code
              ] || PRIMER_PAYMENT_ERRORS.COMMON_ERROR,
            ),
          ),
        )
        dispatch(stopFetching(PURCHASE))
        dispatch(setIsPrimerRetryProcessing(false))

        if (
          paymentMethod === PaymentMethod.CREDIT_CARD &&
          !isFirstPaymentRetryPassed
        ) {
          dispatch(updatePrimerClientSessionAction(retryPaymentCallback))
        }

        return
      }

      dispatch(
        setPrimerClientSessionTokenAction(response.data.payment.client_token),
      )

      if (response.data.payment.client_token) {
        dispatch(setPrimerPaymentId(response.data.payment.payment_id))

        handler.continueWithNewClientToken(response.data.payment.client_token)
        return
      }

      dispatch(
        primerConfirmPaymentAction(response.data.payment.payment_id, handler),
      )

      const predictedLtvResponse = await userApi.getPredictedLtv({ uuid })
      if (
        !!predictedLtvResponse.success &&
        predictedLtvResponse.status === 200
      ) {
        predictedLtv = predictedLtvResponse.data.predicted_ltv
      }

      if (!config?.dynamicUpsellPages) {
        dispatch(
          sendUserConfigAction({
            dynamic_upsell_pages: UPSELL_PAGES_MAP[cohortToUse],
          }),
        )
      }

      logSuccessfulPayment({
        ...commonPurchaseSuccessParams,
        productId: response.data.purchase.product_id,
        price: currentPrice,
        trialPrice,
        trialPeriodDays,
        subscriptionId: response.data.purchase.subscription_id,
        paymentMethod,
        paymentSystem: PaymentSystem.PRIMER,
        discountApplied: response.data.purchase.discount_applied,
        predictedLtv,
        processorName,
      })

      handleGoaffproRefPurchase({
        subscriptionId: response.data.purchase.subscription_id,
        purchaseAmount: trialPrice || currentPrice,
        currency,
        discount: response.data.purchase.discount_applied,
      })

      dispatch(
        sendUserConfigAction({
          payment_currency: currency,
          payment_method: paymentMethod,
          is_download_visited: false,
          subscription_price: currentPrice,
          subscription_duration: `${periodQuantity}${periodName}`,
          price_id: priceId,
          trial_price: trialPrice,
          trial_period: `${trialPeriodDays}`,
          payment_system: PaymentSystem.PRIMER,
          discount_applied: !!response.data.purchase.discount_applied,
        }),
      )
    } catch (error: any) {
      dispatch(setErrorAction(error.toString()))
      dispatch(setIsPrimerRetryProcessing(false))
      dispatch(stopFetching(PURCHASE))
      handler.handleFailure(error.toString())
    }
  }
}

export function primerResumePurchaseAction(
  currentPaymentId: string | undefined,
  resumeToken: string,
  handler: OnResumeSuccessHandler,
  retryPaymentCallback: () => void,
): any {
  return async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ) => {
    const state = getState()
    const uuid = selectUUID(state)
    const paymentMethod = selectPaymentMethod(state)
    const cohort = selectCurrentVariantCohort(state) as Cohort
    const paymentId = selectUserPaymentId(state)
    const isFirstPaymentRetryPassed = selectIsFirstPaymentRetryPassed(state)
    const trialPeriodDays = selectSubscriptionTrialPeriodDays(state)
    const currentPrice = selectSubscriptionFullPrice(state)
    const trialPrice = selectSubscriptionTrialPeriodPrice(state)
    const currency = selectCurrency(state)
    const periodName = selectSubscriptionPeriodName(state)
    const periodQuantity = selectSubscriptionPeriodQuantity(state)
    const priceId = selectSubscriptionLookupKey(state)
    const parentCohort = selectCurrentVariantParentCohort(state)
    const config = selectUserConfigData(state)
    const cohortToUse = parentCohort || cohort || Cohort.LUVLY_INTRO_FAST
    const processorName = selectPrimerProcessorName(state)

    let predictedLtv

    const commonPurchaseSuccessParams =
      getStripePurchaseSuccessEventParams(state)
    const commonPurchaseFailedParams = getStripePurchaseFailedEventParams(state)

    dispatch(startFetching(PURCHASE))

    try {
      const response = await paymentApi.resumePrimerSubscription({
        uuid,
        paymentId: currentPaymentId || paymentId,
        resumeToken,
      })

      if (!response.data && !window.navigator.onLine) {
        dispatch(setErrorAction(i18n.t(PRIMER_PAYMENT_ERRORS.COMMON_ERROR)))
        dispatch(stopFetching(PURCHASE))
        dispatch(setIsPrimerRetryProcessing(false))
        return
      }

      if (!response.data.payment.is_success) {
        const error = {
          type: response.data.payment.status_reason.type,
          description: response.data.payment.status_reason.message,
          code: response.data.payment.status_reason.code,
        }

        eventLogger.logPurchaseFailed({
          ...commonPurchaseFailedParams,
          paymentMethod,
          error,
          paymentSystem: PaymentSystem.PRIMER,
          processorName,
        })

        handler.handleFailure(response.data.payment.status_reason.message)

        dispatch(
          setErrorAction(
            i18n.t(
              PRIMER_PAYMENT_ERRORS[
                response?.data.payment.status_reason.code
              ] || PRIMER_PAYMENT_ERRORS.COMMON_ERROR,
            ),
          ),
        )

        dispatch(stopFetching(PURCHASE))
        dispatch(setIsPrimerRetryProcessing(false))

        if (
          paymentMethod === PaymentMethod.CREDIT_CARD &&
          !isFirstPaymentRetryPassed
        ) {
          dispatch(updatePrimerClientSessionAction(retryPaymentCallback))
        }

        return
      }

      if (response.data.payment.client_token) {
        handler.continueWithNewClientToken(response.data.payment.client_token)
        return
      }

      dispatch(
        primerConfirmPaymentAction(response.data.payment.payment_id, handler),
      )
      dispatch(
        setPrimerClientSessionTokenAction(response.data.payment.client_token),
      )

      const predictedLtvResponse = await userApi.getPredictedLtv({ uuid })
      if (
        !!predictedLtvResponse.success &&
        predictedLtvResponse.status === 200
      ) {
        predictedLtv = predictedLtvResponse.data.predicted_ltv
      }

      if (!config?.dynamicUpsellPages) {
        dispatch(
          sendUserConfigAction({
            dynamic_upsell_pages: UPSELL_PAGES_MAP[cohortToUse],
          }),
        )
      }

      logSuccessfulPayment({
        ...commonPurchaseSuccessParams,
        productId: response.data.purchase.product_id,
        price: currentPrice,
        trialPrice,
        trialPeriodDays,
        subscriptionId: response.data.purchase.subscription_id,
        paymentMethod,
        paymentSystem: PaymentSystem.PRIMER,
        discountApplied: response.data.purchase.discount_applied,
        predictedLtv,
        processorName,
      })

      handleGoaffproRefPurchase({
        subscriptionId: response.data.purchase.subscription_id,
        purchaseAmount: trialPrice || currentPrice,
        currency,
        discount: response.data.purchase.discount_applied,
      })

      dispatch(
        sendUserConfigAction({
          payment_currency: currency,
          payment_method: paymentMethod,
          is_download_visited: false,
          subscription_price: currentPrice,
          subscription_duration: `${periodQuantity}${periodName}`,
          price_id: priceId,
          trial_price: trialPrice,
          trial_period: `${trialPeriodDays}`,
          payment_system: PaymentSystem.PRIMER,
          discount_applied: !!response.data.purchase.discount_applied,
        }),
      )

      handler.handleSuccess()
    } catch (error: any) {
      dispatch(setErrorAction(error.toString()))
      dispatch(setIsPrimerRetryProcessing(false))
      dispatch(stopFetching(PURCHASE))
      handler.handleFailure(error.toString())
    }
  }
}

export const purchaseAction =
  ({
    stripe,
    paymentPageId,
    card,
    name,
    createPaymentResFromDigitalWallet,
  }: {
    stripe: Stripe
    paymentPageId: string
    card?: StripeCardNumberElement
    name?: string
    createPaymentResFromDigitalWallet?: PaymentRequestPaymentMethodEvent
  }): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const priceId = selectSubscriptionLookupKey(state)
    const trialPrice = selectSubscriptionTrialPeriodPrice(state)
    const uuid = selectUUID(state)
    const currentPrice = selectSubscriptionFullPrice(state)
    const trialPeriodDays = selectSubscriptionTrialPeriodDays(state)
    const currency = selectCurrency(state)
    const periodName = selectSubscriptionPeriodName(state)
    const periodQuantity = selectSubscriptionPeriodQuantity(state)
    const cohort = selectCurrentVariantCohort(state) as Cohort
    const planId = selectInAppId(state)
    const config = selectUserConfigData(state)
    const parentCohort = selectCurrentVariantParentCohort(state)
    const hasPrintablePdf = selectIsPrintablePdfEnabled(state)

    const cohortToUse = parentCohort || cohort || Cohort.LUVLY_INTRO_FAST
    let predictedLtv

    if (!priceId || !currentPrice) {
      console.error('Error: no subscriptions plan selected')
      return
    }

    dispatch(startFetching(PURCHASE))

    if (cohortToUse === Cohort.LUVLY_PRINTABLE && hasPrintablePdf) {
      dispatch(savePlanAdditionsAction([PlanAddition.PRINTABLE_PDF]))
    }

    const paymentMethod =
      createPaymentResFromDigitalWallet?.paymentMethod?.card?.wallet?.type

    const commonPurchaseStartedParams =
      getStripePurchaseStartedEventParams(state)
    const commonPurchaseSuccessParams =
      getStripePurchaseSuccessEventParams(state)
    const commonPurchaseFailedParams = getStripePurchaseFailedEventParams(state)

    eventLogger.logPurchaseStarted({
      paymentMethod,
      paymentSystem: PaymentSystem.STRIPE,
      ...commonPurchaseStartedParams,
    })

    sendAddToCartEvents()

    try {
      const createPaymentResponse =
        card && !createPaymentResFromDigitalWallet
          ? await stripe.createPaymentMethod({
              type: 'card',
              card,
              billing_details: { name: name || DEFAULT_CARDHOLDER_NAME },
            })
          : (createPaymentResFromDigitalWallet as PaymentMethodResult)

      if (
        !createPaymentResponse?.paymentMethod &&
        createPaymentResponse?.error
      ) {
        const {
          error: { type, code, message },
        } = createPaymentResponse

        eventLogger.logPurchaseFailed({
          error: {
            type,
            code,
            description: message,
          },
          paymentMethod,
          ...commonPurchaseFailedParams,
        })
        dispatch(setErrorAction(message || type))
        dispatch(stopFetching(PURCHASE))
        createPaymentResFromDigitalWallet?.complete('fail')
        return
      }

      const createSubscriptionResponse = await paymentApi.createSubscription({
        uuid,
        planId: Number(planId),
        trialPeriodDays,
        cohort,
      })

      if (
        !createSubscriptionResponse.success ||
        !createSubscriptionResponse.data
      ) {
        switch (createSubscriptionResponse.status) {
          case 402:
            dispatch(setErrorAction(i18n.t('purchase1.insufficientFunds')))
            break
          case 404:
            dispatch(setErrorAction(i18n.t('purchase1.linkIsNotValid')))
            break
          case 409:
            dispatch(setErrorAction(i18n.t('purchase1.haveSubscription')))
            break
          default:
            dispatch(setErrorAction(i18n.t('login.somethingWentWrongError')))
        }

        dispatch(stopFetching(PURCHASE))
        createPaymentResFromDigitalWallet?.complete('fail')
        return
      }

      dispatch(
        setPaymentClientSecretAction(
          createSubscriptionResponse.data.purchase.client_secret,
        ),
      )
      dispatch(
        setTrialPeriodDaysAction(
          createSubscriptionResponse.data.purchase.trial_period_days,
        ),
      )
      dispatch(
        setCreatedSubscriptionIdAction(
          createSubscriptionResponse.data.purchase.subscription_id,
        ),
      )

      const cardPaymentResponseFirst = await stripe.confirmCardPayment(
        createSubscriptionResponse.data.purchase.client_secret,
        {
          payment_method: createPaymentResponse.paymentMethod.id,
          save_payment_method: true,
          return_url: getRedirectUrl(paymentPageId),
        },
      )

      if (
        !cardPaymentResponseFirst?.paymentIntent &&
        cardPaymentResponseFirst?.error
      ) {
        logFailedPayment({
          paymentResponse: cardPaymentResponseFirst.error,
          paymentMethod,
          ...commonPurchaseFailedParams,
        })

        if (!checkIsRetryAllowed(cardPaymentResponseFirst)) {
          dispatch(
            setErrorAction(
              getErrorActionPayload(cardPaymentResponseFirst.error),
            ),
          )
          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('fail')
          return
        }

        const retryResponseFirst = await paymentApi.retryPayment({
          uuid,
          stripeError: cardPaymentResponseFirst.error as StripeError,
        })

        if (!retryResponseFirst.data.should_retry) {
          dispatch(
            setErrorAction(
              getErrorActionPayload(cardPaymentResponseFirst.error),
            ),
          )
          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('fail')
          return
        }

        dispatch(
          setPaymentClientSecretAction(
            retryResponseFirst.data.subscription.client_secret,
          ),
        )
        dispatch(
          setTrialPeriodDaysAction(
            retryResponseFirst.data.subscription.trial_period_days,
          ),
        )
        dispatch(
          setCreatedSubscriptionIdAction(
            retryResponseFirst.data.subscription.subscription_id,
          ),
        )

        const cardPaymentResponseSecond = await stripe.confirmCardPayment(
          retryResponseFirst.data.subscription.client_secret,
          {
            payment_method: createPaymentResponse.paymentMethod.id,
            save_payment_method: true,
            return_url: getRedirectUrl(paymentPageId),
          },
        )

        if (
          cardPaymentResponseSecond?.paymentIntent &&
          !cardPaymentResponseSecond?.error
        ) {
          const predictedLtvResponse = await userApi.getPredictedLtv({ uuid })
          if (
            !!predictedLtvResponse.success &&
            predictedLtvResponse.status === 200
          ) {
            predictedLtv = predictedLtvResponse.data.predicted_ltv
          }

          if (!config?.dynamicUpsellPages) {
            dispatch(
              sendUserConfigAction({
                dynamic_upsell_pages: UPSELL_PAGES_MAP[cohortToUse],
              }),
            )
          }

          logSuccessfulPayment({
            ...commonPurchaseSuccessParams,
            price:
              cardPaymentResponseSecond.paymentIntent.amount / CENTS_IN_DOLLAR,
            trialPrice:
              cardPaymentResponseSecond.paymentIntent.amount / CENTS_IN_DOLLAR,
            productId: createSubscriptionResponse.data.purchase.product_id,
            trialPeriodDays:
              retryResponseFirst.data.subscription.trial_period_days,
            subscriptionId:
              retryResponseFirst.data.subscription.subscription_id,
            discountApplied:
              retryResponseFirst.data.subscription.discount_applied,
            paymentMethod,
            paymentSystem: PaymentSystem.STRIPE,
            predictedLtv,
          })

          handleGoaffproRefPurchase({
            subscriptionId:
              retryResponseFirst.data.subscription.subscription_id,
            purchaseAmount:
              cardPaymentResponseSecond.paymentIntent.amount / CENTS_IN_DOLLAR,
            currency,
          })

          dispatch(stopFetching(PURCHASE))
          dispatch(
            sendUserConfigAction(
              {
                payment_currency: currency,
                payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
                payment_system: PaymentSystem.STRIPE,
                is_download_visited: false,
                subscription_price: `${currentPrice}`,
                subscription_duration: `${periodQuantity}${periodName}`,
                price_id: priceId,
                trial_price: `${trialPrice}`,
                trial_period: `${trialPeriodDays}`,
                discount_applied:
                  !!retryResponseFirst.data.subscription.discount_applied,
              },
              null,
            ),
          )
          createPaymentResFromDigitalWallet?.complete('success')
          return
        }

        logFailedPayment({
          paymentResponse: cardPaymentResponseSecond.error,
          paymentMethod,
          ...commonPurchaseFailedParams,
        })

        if (!checkIsRetryAllowed(cardPaymentResponseSecond)) {
          dispatch(
            setErrorAction(
              getErrorActionPayload(cardPaymentResponseSecond.error),
            ),
          )
          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('fail')
          return
        }

        const retryResponseSecond = await paymentApi.retryPayment({
          uuid,
          stripeError: cardPaymentResponseSecond.error as StripeError,
        })

        if (!retryResponseSecond.data.should_retry) {
          dispatch(
            setErrorAction(
              getErrorActionPayload(cardPaymentResponseSecond.error),
            ),
          )
          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('fail')
          return
        }

        dispatch(
          setPaymentClientSecretAction(
            retryResponseSecond.data.subscription.client_secret,
          ),
        )
        dispatch(
          setTrialPeriodDaysAction(
            retryResponseSecond.data.subscription.trial_period_days,
          ),
        )
        dispatch(
          setCreatedSubscriptionIdAction(
            retryResponseSecond.data.subscription.subscription_id,
          ),
        )

        const cardPaymentResponseThird = await stripe.confirmCardPayment(
          retryResponseSecond.data.subscription.client_secret,
          {
            payment_method: createPaymentResponse.paymentMethod.id,
            save_payment_method: true,
            return_url: getRedirectUrl(paymentPageId),
          },
        )

        if (
          cardPaymentResponseThird?.paymentIntent &&
          !cardPaymentResponseThird?.error
        ) {
          const predictedLtvResponse = await userApi.getPredictedLtv({ uuid })
          if (
            !!predictedLtvResponse.success &&
            predictedLtvResponse.status === 200
          ) {
            predictedLtv = predictedLtvResponse.data.predicted_ltv
          }

          if (!config?.dynamicUpsellPages) {
            dispatch(
              sendUserConfigAction({
                dynamic_upsell_pages: UPSELL_PAGES_MAP[cohortToUse],
              }),
            )
          }

          logSuccessfulPayment({
            ...commonPurchaseSuccessParams,
            price:
              cardPaymentResponseThird.paymentIntent.amount / CENTS_IN_DOLLAR,
            trialPrice:
              cardPaymentResponseThird.paymentIntent.amount / CENTS_IN_DOLLAR,
            productId: createSubscriptionResponse.data.purchase.product_id,
            trialPeriodDays:
              retryResponseSecond.data.subscription.trial_period_days,
            subscriptionId:
              retryResponseSecond.data.subscription.subscription_id,
            discountApplied:
              retryResponseSecond.data.subscription.discount_applied,
            paymentMethod,
            paymentSystem: PaymentSystem.STRIPE,
            predictedLtv,
          })

          handleGoaffproRefPurchase({
            subscriptionId:
              retryResponseSecond.data.subscription.subscription_id,
            purchaseAmount:
              cardPaymentResponseThird.paymentIntent.amount / CENTS_IN_DOLLAR,
            currency,
          })

          dispatch(stopFetching(PURCHASE))
          dispatch(
            sendUserConfigAction(
              {
                payment_currency: currency,
                payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
                is_download_visited: false,
                subscription_price: `${currentPrice}`,
                subscription_duration: `${periodQuantity}${periodName}`,
                price_id: priceId,
                trial_price: `${trialPrice}`,
                trial_period: `${trialPeriodDays}`,
                payment_system: PaymentSystem.STRIPE,
                discount_applied:
                  !!retryResponseSecond.data.subscription.discount_applied,
              },
              null,
            ),
          )
          createPaymentResFromDigitalWallet?.complete('success')
          return
        }

        logFailedPayment({
          paymentResponse: cardPaymentResponseThird.error,
          paymentMethod,
          ...commonPurchaseFailedParams,
        })

        if (!checkIsRetryAllowed(cardPaymentResponseThird)) {
          dispatch(
            setErrorAction(
              getErrorActionPayload(cardPaymentResponseThird.error),
            ),
          )
          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('fail')
          return
        }

        const retryResponseThird = await paymentApi.retryPayment({
          uuid,
          stripeError: cardPaymentResponseThird.error as StripeError,
        })

        if (!retryResponseThird.data.should_retry) {
          dispatch(
            setErrorAction(
              getErrorActionPayload(cardPaymentResponseThird.error),
            ),
          )
          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('fail')
          return
        }

        dispatch(
          setPaymentClientSecretAction(
            retryResponseThird.data.subscription.client_secret,
          ),
        )
        dispatch(
          setTrialPeriodDaysAction(
            retryResponseThird.data.subscription.trial_period_days,
          ),
        )
        dispatch(
          setCreatedSubscriptionIdAction(
            retryResponseThird.data.subscription.subscription_id,
          ),
        )

        const cardPaymentResponseFourth = await stripe.confirmCardPayment(
          retryResponseThird.data.subscription.client_secret,
          {
            payment_method: createPaymentResponse.paymentMethod.id,
            save_payment_method: true,
            return_url: getRedirectUrl(paymentPageId),
          },
        )

        if (
          cardPaymentResponseFourth?.paymentIntent &&
          !cardPaymentResponseFourth?.error
        ) {
          const predictedLtvResponse = await userApi.getPredictedLtv({ uuid })
          if (
            !!predictedLtvResponse.success &&
            predictedLtvResponse.status === 200
          ) {
            predictedLtv = predictedLtvResponse.data.predicted_ltv
          }

          if (!config?.dynamicUpsellPages) {
            dispatch(
              sendUserConfigAction({
                dynamic_upsell_pages: UPSELL_PAGES_MAP[cohortToUse],
              }),
            )
          }

          logSuccessfulPayment({
            ...commonPurchaseSuccessParams,
            price:
              cardPaymentResponseFourth.paymentIntent.amount / CENTS_IN_DOLLAR,
            trialPrice:
              cardPaymentResponseFourth.paymentIntent.amount / CENTS_IN_DOLLAR,
            productId: createSubscriptionResponse.data.purchase.product_id,
            trialPeriodDays:
              retryResponseThird.data.subscription.trial_period_days,
            subscriptionId:
              retryResponseThird.data.subscription.subscription_id,
            discountApplied:
              retryResponseThird.data.subscription.discount_applied,
            paymentMethod,
            paymentSystem: PaymentSystem.STRIPE,
            predictedLtv,
          })

          handleGoaffproRefPurchase({
            subscriptionId:
              retryResponseThird.data.subscription.subscription_id,
            purchaseAmount:
              cardPaymentResponseFourth.paymentIntent.amount / CENTS_IN_DOLLAR,
            currency,
          })

          dispatch(stopFetching(PURCHASE))
          dispatch(
            sendUserConfigAction(
              {
                payment_currency: currency,
                payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
                is_download_visited: false,
                subscription_price: `${currentPrice}`,
                subscription_duration: `${periodQuantity}${periodName}`,
                price_id: priceId,
                trial_price: `${trialPrice}`,
                trial_period: `${trialPeriodDays}`,
                payment_system: PaymentSystem.STRIPE,
                discount_applied:
                  !!retryResponseThird.data.subscription.discount_applied,
              },
              null,
            ),
          )
          createPaymentResFromDigitalWallet?.complete('success')
          return
        }

        logFailedPayment({
          paymentResponse: cardPaymentResponseFourth.error,
          paymentMethod,
          ...commonPurchaseFailedParams,
        })

        dispatch(
          setErrorAction(
            getErrorActionPayload(cardPaymentResponseFourth.error),
          ),
        )

        // Needed for reset invoice on BE
        await paymentApi.retryPayment({
          uuid,
          stripeError: cardPaymentResponseFourth.error as StripeError,
        })

        dispatch(stopFetching(PURCHASE))
        createPaymentResFromDigitalWallet?.complete('fail')
        return
      }

      const { paymentIntent } = cardPaymentResponseFirst

      if (paymentIntent.status === 'requires_payment_method') {
        eventLogger.logPurchaseFailed({
          error: {
            type: 'requires_payment_method',
          },
          paymentMethod,
          ...commonPurchaseFailedParams,
        })
        dispatch(setErrorAction(i18n.t('purchase1.paymentFailed')))
        dispatch(stopFetching(PURCHASE))
        createPaymentResFromDigitalWallet?.complete('fail')
        return
      }

      const threeDSecureURL = paymentIntent.next_action?.redirect_to_url?.url

      if (paymentIntent.status === 'requires_action' && threeDSecureURL) {
        dispatch(set3DSecureIframeUrlAction(threeDSecureURL))
        dispatch(stopFetching(PURCHASE))
        return
      }

      const predictedLtvResponse = await userApi.getPredictedLtv({ uuid })
      if (
        !!predictedLtvResponse.success &&
        predictedLtvResponse.status === 200
      ) {
        predictedLtv = predictedLtvResponse.data.predicted_ltv
      }

      if (!config?.dynamicUpsellPages) {
        dispatch(
          sendUserConfigAction({
            dynamic_upsell_pages: UPSELL_PAGES_MAP[cohortToUse],
          }),
        )
      }

      logSuccessfulPayment({
        price: currentPrice,
        productId: createSubscriptionResponse.data.purchase.product_id,
        trialPeriodDays:
          createSubscriptionResponse.data.purchase.trial_period_days,
        subscriptionId:
          createSubscriptionResponse.data.purchase.subscription_id,
        paymentMethod,
        ...commonPurchaseSuccessParams,
        paymentSystem: PaymentSystem.STRIPE,
        predictedLtv,
      })

      handleGoaffproRefPurchase({
        subscriptionId:
          createSubscriptionResponse.data.purchase.subscription_id,
        purchaseAmount: trialPrice || currentPrice,
        currency,
      })

      dispatch(stopFetching(PURCHASE))
      dispatch(
        sendUserConfigAction(
          {
            payment_currency: currency,
            payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
            is_download_visited: false,
            subscription_price: `${currentPrice}`,
            subscription_duration: `${periodQuantity}${periodName}`,
            price_id: priceId,
            trial_price: `${trialPrice}`,
            trial_period: `${trialPeriodDays}`,
            payment_system: PaymentSystem.STRIPE,
          },
          null,
        ),
      )
      createPaymentResFromDigitalWallet?.complete('success')
    } catch (error: any) {
      dispatch(setErrorAction(error.toString()))
      dispatch(stopFetching(PURCHASE))
      createPaymentResFromDigitalWallet?.complete('fail')
    }
  }

export const purchaseUpgrade =
  (): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const priceId = selectSubscriptionLookupKey(state)
    const uuid = selectUUID(state)
    const fullPrice = selectUpgradeSubscriptionFullPrice(state)
    const amountToPay = selectUpgradeSubscriptionPrice(state)
    const currency = selectCurrency(state)
    const screenName = selectScreenName(state)
    const stripeAccountId = selectStripeAccountId(state)
    const stripeAccountName = selectStripeAccountName(state)
    const paymentSystem = selectUserPaymentSystem(state)
    const cohort = selectCurrentVariantCohort(state) as Cohort
    const planId = selectInAppId(state)
    const paymentMethod = selectUserPaymentMethod(state) as PaymentMethod
    const processorName = selectPrimerProcessorName(state)

    if (!priceId || !fullPrice) {
      console.error('Error: no subscriptions plan selected')
      return
    }

    dispatch(startFetching(PURCHASE))

    eventLogger.logPurchaseStarted({
      paymentMethod,
      paymentSystem,
      isUpgraded: true,
      amountToPay: `${amountToPay / CENTS_IN_DOLLAR}`,
      priceDetails: { currency, price: fullPrice },
      screenName,
      productId: priceId,
      stripeAccountId,
      stripeAccountName,
      processorName,
    })

    try {
      const createSubscriptionResponse =
        paymentSystem === PaymentSystem.PRIMER
          ? await paymentApi.upgradePrimerSubscription({
              uuid,
              planId: Number(planId),
              cohort,
            })
          : await paymentApi.upgradeSubscription({
              uuid,
              planId: Number(planId),
              cohort,
            })

      if (
        !createSubscriptionResponse.success ||
        !createSubscriptionResponse.data
      ) {
        const errorText = i18n.t(
          getPaymentFailedError(createSubscriptionResponse.data?.decline_code),
        )

        eventLogger.logPurchaseFailed({
          error: {
            type: createSubscriptionResponse.data?.error,
            code: createSubscriptionResponse.data?.decline_code,
            description: errorText,
          },
          paymentMethod,
          isUpgraded: true,
          priceDetails: { currency, price: fullPrice },
          screenName,
          amountToPay: `${amountToPay / CENTS_IN_DOLLAR}`,
          productId: priceId,
          stripeAccountId,
          stripeAccountName,
          paymentSystem,
          processorName,
        })

        dispatch(setErrorAction(i18n.t(errorText)))
        dispatch(stopFetching(PURCHASE))
        return
      }

      dispatch(setIsSubscriptionUpgradedAction(true))

      eventLogger.logPurchaseCompleted({
        priceDetails: {
          price: fullPrice,
          currency,
        },
        paymentMethod,
        utmSource: 'livechat',
        isUpgraded: true,
        amountToPay: `${amountToPay / CENTS_IN_DOLLAR}`,
        screenName,
        productId: createSubscriptionResponse.data.upgrade.product_id,
        stripeAccountId,
        stripeAccountName,
        paymentSystem,
        processorName,
      })

      dispatch(stopFetching(PURCHASE))
    } catch (error: any) {
      dispatch(setErrorAction(error.toString()))
      dispatch(stopFetching(PURCHASE))
    }
  }

export const check3DSecure =
  (stripe: Stripe): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const priceId = selectSubscriptionLookupKey(state)
    const trialPriceId = selectSubscriptionLookupKey(state)
    const currentPrice = selectSubscriptionFullPrice(state)
    const clientSecret = selectPaymentClientSecret(state)
    const trialPeriodDays = selectTrialPeriodDays(state)
    const subscriptionId = selectCreatedSubscriptionId(state)
    const currency = selectCurrency(state)
    const periodName = selectSubscriptionPeriodName(state)
    const periodQuantity = selectSubscriptionPeriodQuantity(state)
    const trialPeriodQuantity = selectSubscriptionTrialPeriodDays(state)
    const trialPrice = selectSubscriptionTrialPeriodPrice(state)
    const uuid = selectUUID(state)
    let predictedLtv

    if (!priceId || !currentPrice) {
      console.error('Error: no subscriptions plan selected')
      return
    }

    if (!clientSecret) {
      console.error('Error: client secret is needed')
      return
    }

    dispatch(startFetching(CHECK_3D_SECURE))

    const response = await stripe.retrievePaymentIntent(clientSecret)
    const commonPurchaseSuccessParams =
      getStripePurchaseSuccessEventParams(state)
    const commonPurchaseFailedParams = getStripePurchaseFailedEventParams(state)

    if (response.paymentIntent?.status === 'succeeded') {
      const predictedLtvResponse = await userApi.getPredictedLtv({ uuid })
      if (
        !!predictedLtvResponse.success &&
        predictedLtvResponse.status === 200
      ) {
        predictedLtv = predictedLtvResponse.data.predicted_ltv
      }
      logSuccessfulPayment({
        price: currentPrice,
        trialPeriodDays,
        subscriptionId,
        productId: trialPriceId
          ? createIntroOfferProductId({
              priceId,
              trialPriceId,
              trialPeriodQuantity,
            })
          : priceId,
        ...commonPurchaseSuccessParams,
        paymentSystem: PaymentSystem.STRIPE,
        predictedLtv,
      })

      handleGoaffproRefPurchase({
        subscriptionId,
        purchaseAmount: trialPrice || currentPrice,
        currency,
      })

      dispatch(reset3DSecureIframeUrlAction())
      dispatch(
        sendUserConfigAction(
          {
            payment_currency: currency,
            payment_method: PaymentMethod.CREDIT_CARD,
            is_download_visited: false,
            subscription_price: `${currentPrice}`,
            subscription_duration: `${periodQuantity}${periodName}`,
            price_id: priceId,
            trial_price: `${trialPrice}`,
            trial_period: `${trialPeriodDays}`,
          },
          null,
        ),
      )
      return
    }

    if (response.paymentIntent?.status === 'requires_payment_method') {
      eventLogger.logPurchaseFailed({
        error: {
          type: 'requires_payment_method',
        },
        ...commonPurchaseFailedParams,
      })
      dispatch(reset3DSecureIframeUrlAction())
      dispatch(setErrorAction(i18n.t('purchase1.paymentFailed')))
      dispatch(stopFetching(CHECK_3D_SECURE))
      return
    }

    if (response.error) {
      const {
        error: { type, code, message },
      } = response

      eventLogger.logPurchaseFailed({
        error: {
          type,
          code,
          description: message,
        },
        ...commonPurchaseFailedParams,
      })
      dispatch(reset3DSecureIframeUrlAction())
      dispatch(setErrorAction(message || type))
      dispatch(stopFetching(CHECK_3D_SECURE))
      return
    }

    dispatch(reset3DSecureIframeUrlAction())
    dispatch(setErrorAction(i18n.t('purchase1.3dSecureError')))
    dispatch(stopFetching(CHECK_3D_SECURE))
  }
